import { useMemo } from 'react';
import { Skeleton, Box } from '@mui/material';
import { ApolloError } from '@apollo/client';

import {
  BarChart,
  Typography,
  BarChartProps,
  roundNumberToTwoDecimals,
} from '@occc/ui-design-system';
import { TimeSeriesDataPoint } from '@occc/graphql';
import { parseDate, formatDateTime, DateTimeFormat } from '@occc-app/lib/date';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';

import { MissingStoresRawItem } from './scan-data-missing-stores-chart.types';

import { getBarColor } from '../bar-chart/get-bar-color';
import { CHART_HEIGHT } from '../scan-data-overview.constants';
import { calculateMissingStoresPercentage } from '../scan-data-overview.utils';

export type ScanDataMissingStoresChartProps = {
  loading: boolean;
  error: ApolloError | undefined;
  data: TimeSeriesDataPoint[] | undefined;
  submittedStores: TimeSeriesDataPoint[] | undefined;
};

export const ScanDataMissingStoresChart = ({
  loading,
  error,
  data,
  submittedStores,
}: ScanDataMissingStoresChartProps) => {
  const submittedStoresByDate = useMemo(() => {
    const map = new Map<string, number>();
    submittedStores?.forEach(({ date, value }) => {
      map.set(date, value);
    });
    return map;
  }, [submittedStores]);

  const chartItems = useMemo(
    () =>
      data?.map(({ date, value }) => ({
        name: formatDateTime(parseDate(date), DateTimeFormat.MonthAndDateSlashed),
        value,
        rawItem: { date, value, submittedStoresCount: submittedStoresByDate.get(date) ?? 0 },
      })),
    [data, submittedStoresByDate],
  );

  const buildTooltipProps: BarChartProps<MissingStoresRawItem>['buildTooltipProps'] = useMemo(
    () =>
      ({ payload }) => {
        const missingStoresCount = Array.isArray(payload) ? payload[0]?.value ?? 0 : 0;
        const submittedStoresCount = payload?.[0]?.payload?.rawItem?.submittedStoresCount ?? 0;

        const percentage = calculateMissingStoresPercentage(
          missingStoresCount,
          submittedStoresCount,
        );

        return {
          items: [
            {
              label: 'Number of missing stores',
              value: separateEveryThousandByComma(missingStoresCount),
            },
            {
              label: 'Percentage',
              value: `${roundNumberToTwoDecimals(percentage)}%`,
            },
          ],
        };
      },
    [],
  );

  if (loading && !data) {
    return <Skeleton variant='rectangular' width='100%' height={CHART_HEIGHT} />;
  }

  if (error || !chartItems) {
    return (
      <Box p={4} height={CHART_HEIGHT}>
        <Typography color='error'>Something went wrong. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box height={CHART_HEIGHT}>
      <BarChart
        disableSelection
        items={chartItems}
        buildTooltipProps={buildTooltipProps}
        shouldShowLegend={false}
        getBarColor={getBarColor}
        roundedBarCorners
      />
    </Box>
  );
};
